import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  Users,
  Shield,
  X,
  Languages,
  CloudUpload,
  Image,
  Crop,
  RotateCcw,
  Sun,
  Moon,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  UserPlus,
  ChevronRight,
  Briefcase,
  BarChart2,
  Settings
} from 'lucide-react';
import AptiviewLogo from '../../../../assets/AptiviewLogo.png';
import api from '../../../../api/client';
import { setDocumentDirection } from '../../../../utils/rtlUtils';
import LanguageSelectionModal from '../../../Sidebar/components/modals/LanguageSelectionModal';
import CompanyUsersCards from './CompanyUsersCards';
import CompanyUsersTable from './CompanyUsersTable';
import CompanyStatsCards from './CompanyStatsCards';
import InviteAdminModal from './InviteAdminModal';
import UserLoginActivityModal from './UserLoginActivityModal';
import CreditsTab from './CreditsTab';
import NotificationPopup from '../../component/NotificationPopup';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import PageHeader, { PageCard, PageShell } from '../../../common/PageHeader';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import Timeline from '../../component/Timeline';

const DEFAULT_LANGUAGES = [
  { code: 'en', name: 'English', native_name: 'English' },
  { code: 'hi', name: 'Hindi', native_name: 'Hindi' },
  { code: 'bn', name: 'Bengali', native_name: 'বাংলা' },
  { code: 'mr', name: 'Marathi', native_name: 'मराठी' },
  { code: 'ar', name: 'Arabic', native_name: 'العربية' },
  { code: 'es', name: 'Spanish', native_name: 'Español' },
  { code: 'fr', name: 'French', native_name: 'Français' },
  { code: 'pt', name: 'Portuguese', native_name: 'Português' },
  { code: 'zh', name: 'Chinese', native_name: '中文' },
  { code: 'ja', name: 'Japanese', native_name: '日本語' },
  { code: 'ko', name: 'Korean', native_name: '한국어' }
];

const VALID_COMPANY_ADMIN_TABS = ['users', 'credits', 'languages', 'appStatus', 'interviewConfiguration', 'companyLogo'];
const LEGACY_CONFIG_TABS = ['appStatus', 'interviewConfiguration'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const CompanyAdmins = ({ adminToken, onLogout }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (() => {
    const tab = searchParams.get('tab') || 'users';
    return VALID_COMPANY_ADMIN_TABS.includes(tab) ? tab : 'users';
  })();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  usePageLoading(loading || (companies.length > 0 && !selectedCompany), {
    message: t('common.loading', 'Loading…'),
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('admin');
  const [userDetails, setUserDetails] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userName: '' });
  const [loginActivityModal, setLoginActivityModal] = useState({
    open: false,
    userId: null,
    userName: '',
    companyId: null
  });

  // Timeline state
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineUserId, setTimelineUserId] = useState(null);
  const [timelineUserName, setTimelineUserName] = useState('');

  // Notification
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Logo Tab States
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [logoFileInfo, setLogoFileInfo] = useState(null);
  const [logoValidationError, setLogoValidationError] = useState(null);
  const [logoMode, setLogoMode] = useState('light'); // 'light' | 'dark'
  const [showNavbarPreview, setShowNavbarPreview] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserDetails(user);

    // Check access permissions
    const isSuperAdmin = user?.is_superuser === true;
    const isCompanyAdmin = Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;

    if (!isSuperAdmin && !isCompanyAdmin) {
      setError(t('companyAdmins.accessDeniedMessage'));
      setLoading(false);
      return;
    }

    fetchCompanies();
    fetchLanguages();
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  useEffect(() => {
    if (selectedCompany) {
      fetchUsers(selectedCompany);
    }
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompany]);

  useEffect(() => {
    if (!companies.length || !userDetails) return;
    const visibleCompanies = userDetails.is_superuser
      ? companies
      : companies.filter((company) => (
        company.name !== 'Practice Company'
        && Array.isArray(userDetails.admin_companies)
        && userDetails.admin_companies.includes(company.name)
      ));
    if (visibleCompanies.length === 0) return;
    setSelectedCompany((current) => {
      if (current && visibleCompanies.some((company) => company.id === current)) {
        return current;
      }
      return visibleCompanies[0].id;
    });
  }, [companies, userDetails]);


  // Back-compat: redirect location.state.activeTab to query URL
  useEffect(() => {
    const requestedTab = location?.state?.activeTab;
    if (requestedTab && VALID_COMPANY_ADMIN_TABS.includes(requestedTab)) {
      navigate(`/companyadmins?tab=${requestedTab}`, { replace: true, state: {} });
    }
  }, [location?.state, navigate]);

  useEffect(() => {
    if (LEGACY_CONFIG_TABS.includes(activeTab)) {
      navigate('/settings/interview-configuration', { replace: true });
    }
  }, [activeTab, navigate]);

  useEffect(() => {
    if (!userDetails) {
      return;
    }
    const invite = searchParams.get('invite');
    if (invite !== '1') {
      return;
    }
    if (userDetails.is_usermanager) {
      setIsInviteModalOpen(true);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('invite');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, userDetails]);

  const fetchLanguages = async () => {
    try {
      setLanguagesLoading(true);
      setLanguagesError(null);
      const response = await api.get('/languages/');
      const languagesData = response.data.results || response.data || [];
      setLanguages(languagesData);
    } catch (err) {
      console.error('Error fetching languages:', err);
      setLanguages(DEFAULT_LANGUAGES);
      setLanguagesError(t('companyAdmins.failedToLoadLanguages'));
    } finally {
      setLanguagesLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies/');
      const companiesData = response.data.results || response.data;
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(t('companyAdmins.failedToLoadCompanies'));
      setSnackbar({
        open: true,
        message: t('companyAdmins.failedToLoadCompanies'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = useCallback(async (companyId) => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/users?role=admin`);
      setUsers(response.data.results || response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('companyAdmins.failedToLoadUsers'));
      setUsers([]);
      setSnackbar({
        open: true,
        message: t('companyAdmins.failedToLoadUsers'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleRoleFilter = (value) => {
    setRoleFilter(value);
  };

  const handleSendInvite = (email, successMessage) => {
    setSnackbar({
      open: true,
      message: successMessage || t('companyAdmins.successInviteSent', 'Invitation sent to {{email}} successfully.', { email }),
      severity: 'success'
    });
  };

  const openDeleteDialog = (userId, userName) => {
    setDeleteDialog({ open: true, userId, userName });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await api.delete(`/v1/admin/remove-admin/${deleteDialog.userId}/`);
      setSnackbar({
        open: true,
        message: t('companyAdmins.adminRemovedSuccess', 'Admin {{name}} removed successfully.', { name: deleteDialog.userName }),
        severity: 'success'
      });
      // Refresh user list
      if (selectedCompany) {
        fetchUsers(selectedCompany);
      }
    } catch (err) {
      console.error('Error removing admin:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.failedToRemoveAdmin', 'Failed to remove admin.'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, userId: null, userName: '' });
    }
  };

  const handleToggleUserManager = async (userId, newValue) => {
    try {
      const response = await api.patch(`/v1/admin/toggle-user-manager/${userId}/`, {
        usermanager: newValue
      });

      setSnackbar({
        open: true,
        message: response.data?.message || t('companyAdmins.userManagerUpdated', 'User Manager status updated successfully.'),
        severity: 'success'
      });

      // Refresh user list
      if (selectedCompany) {
        fetchUsers(selectedCompany);
      }
    } catch (err) {
      console.error('Error toggling user manager status:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.failedToUpdateUserManager', 'Failed to update User Manager status.'),
        severity: 'error'
      });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
  };

  // Client-side filtering
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      user.user.firstName?.toLowerCase().includes(searchLower) ||
      user.user.lastName?.toLowerCase().includes(searchLower) ||
      user.user.email?.toLowerCase().includes(searchLower) ||
      user.user.username?.toLowerCase().includes(searchLower) ||
      (user.user.profile?.mobile && user.user.profile.mobile.includes(searchTerm)) ||
      (user.user.profile?.aadhaar_number && user.user.profile.aadhaar_number.includes(searchTerm));

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  // Sort users based on sortConfig
  const sortedUsers = React.useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortConfig.key.includes('.')) {
        const keys = sortConfig.key.split('.');
        aValue = keys.reduce((obj, key) => obj?.[key], a);
        bValue = keys.reduce((obj, key) => obj?.[key], b);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      // Handle string comparison
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const availableUiLanguages = useMemo(() => {
    const fromUser = userDetails?.available_languages;
    if (Array.isArray(fromUser) && fromUser.length > 1) {
      return fromUser;
    }
    return ['en', 'hi', 'ar', 'fr', 'es', 'zh', 'ja', 'ko', 'bn', 'pt', 'mr'];
  }, [userDetails?.available_languages]);

  const uiLanguageOptions = useMemo(
    () => [
      { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/gb.png' },
      { code: 'hi', name: 'हिंदी', flag: 'https://flagcdn.com/w40/in.png' },
      { code: 'bn', name: 'বাংলা', flag: 'https://flagcdn.com/w40/bd.png' },
      { code: 'mr', name: 'मराठी', flag: 'https://flagcdn.com/w40/in.png' },
      { code: 'ar', name: 'العربية', flag: 'https://flagcdn.com/w40/sa.png' },
      { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w40/es.png' },
      { code: 'fr', name: 'Français', flag: 'https://flagcdn.com/w40/fr.png' },
      { code: 'pt', name: 'Português', flag: 'https://flagcdn.com/w40/br.png' },
      { code: 'zh', name: '中文', flag: 'https://flagcdn.com/w40/cn.png' },
      { code: 'ja', name: '日本語', flag: 'https://flagcdn.com/w40/jp.png' },
      { code: 'ko', name: '한국어', flag: 'https://flagcdn.com/w40/kr.png' },
    ].filter((lang) => availableUiLanguages.includes(lang.code)),
    [availableUiLanguages]
  );

  const userLanguagesForModal = useMemo(
    () =>
      uiLanguageOptions.map((lang) => ({
        code: lang.code,
        name: lang.name,
        flag: (
          <img
            src={lang.flag}
            width="24"
            height="24"
            style={{ borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' }}
            alt={lang.name}
          />
        ),
      })),
    [uiLanguageOptions]
  );

  const currentUiLanguage =
    uiLanguageOptions.find((lang) => lang.code === i18n.language) || uiLanguageOptions[0];

  const getUserStats = () => {
    const totalCount = filteredUsers.length;
    const adminCount = filteredUsers.filter(u => u.role === 'admin').length;
    const candidateCount = filteredUsers.filter(u => u.role === 'candidate').length;
    const employeeCount = filteredUsers.filter(u => u.role === 'employee').length;

    return { totalCount, adminCount, candidateCount, employeeCount };
  };

  const enabledLanguageCodes = userDetails?.available_languages || [];
  const stats = getUserStats();

  if (error && !loading) {
    return (
      <PageShell fill={false}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-red-800">{t('companyAdmins.accessDenied')}</h3>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  if (LEGACY_CONFIG_TABS.includes(activeTab)) {
    return <Navigate to="/settings/interview-configuration" replace />;
  }

  const getActiveSectionTitle = () => {
    switch (activeTab) {
      case 'users':
        return t('companyAdmins.tabUserManagement');
      case 'credits':
        return t('companyAdmins.creditsTab.tabTitle', 'Credits');
      case 'languages':
        return t('nav.localization', 'Localization');
      case 'companyLogo':
        return t('nav.branding', 'Branding');
      default:
        return t('companyAdmins.tabUserManagement');
    }
  };

  const getActiveSectionDescription = () => {
    switch (activeTab) {
      case 'users':
        return t('companyAdmins.pageSubtitle');
      case 'credits':
        return t(
          'companyAdmins.creditsTab.headerDescription',
          'View available credits, track usage and expirations, and review detailed credit activity for your account.'
        );
      case 'languages':
        return t('companyAdmins.languagesSectionDescription');
      case 'companyLogo':
        return t('companyAdmins.companyLogoDescription');
      default:
        return t('companyAdmins.pageSubtitle');
    }
  };

  const renderSectionHeader = () => (
    <div className="px-6 pt-5 pb-4 border-b border-gray-200 bg-gray-50">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h2
            className="text-base font-semibold text-gray-900"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {getActiveSectionTitle()}
          </h2>
          <p
            className="text-sm text-gray-600 mt-1"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {getActiveSectionDescription()}
          </p>
        </div>
        {activeTab === 'users' && userDetails?.is_usermanager && (
          <button
            type="button"
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white shadow-md transition-all duration-200 hover:opacity-90 hover:scale-[1.02] flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <UserPlus size={18} />
            {t('companyAdmins.inviteAdmin', 'Invite Admin')}
          </button>
        )}
        {activeTab === 'languages' && currentUiLanguage && (
          <button
            type="button"
            onClick={() => setLangModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition-all duration-200 hover:border-sky-300 hover:text-sky-700 flex-shrink-0"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {currentUiLanguage.flag && (
              <img
                src={currentUiLanguage.flag}
                width="22"
                height="22"
                alt=""
                className="rounded-full object-cover border border-gray-200"
              />
            )}
            <span>
              {currentUiLanguage.name} ({currentUiLanguage.code.toUpperCase()})
            </span>
            <ChevronRight size={18} className="text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );

  const renderLanguagesTab = () => {
    return (
      <div className="p-6">
        <div className="mb-6">
          {enabledLanguageCodes.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-sm text-blue-800">
              {t('companyAdmins.languagesNote')}
            </div>
          )}
        </div>

        {languagesError && (
          <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            {languagesError}
          </div>
        )}

        {languagesLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-100"
              />
            ))}
          </div>
        ) : languages.length === 0 ? (
          <div className="text-center py-12">
            <Languages size={48} className="mx-auto mb-4 opacity-50 text-gray-400" />
            <p className="text-lg text-gray-600">{t('companyAdmins.noLanguagesFound')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {languages.map((language) => {
              const isEnabled = enabledLanguageCodes.includes(language.code);
              return (
                <div
                  key={language.code}
                  className={`rounded-xl border p-4 shadow-sm transition-all hover:shadow-md ${isEnabled
                    ? 'border-green-200 bg-gradient-to-br from-green-50 to-white'
                    : 'border-gray-200 bg-white'
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 mb-1">{language.name}</p>
                      {language.native_name && (
                        <p className="text-sm text-gray-600 mb-1">{language.native_name}</p>
                      )}
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        {language.code}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${isEnabled
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}
                    >
                      {isEnabled
                        ? t('companyAdmins.enabledBySuperAdmin')
                        : t('companyAdmins.notEnabledBySuperAdmin')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCompanyLogoTab = () => {
    const activeCompany = companies.find(c => c.id === selectedCompany);

    const resolveMediaUrl = (url) => {
      if (!url) return null;
      if (/^https?:\/\//i.test(url)) return url;
      const apiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api/`;
      return apiUrl.replace(/\/api\/?$/, '') + '/' + url.replace(/^\//, '');
    };

    const getImageInfo = (file) => {
      return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => resolve({ width: 0, height: 0 });
        img.src = URL.createObjectURL(file);
      });
    };

    const formatBytes = (bytes) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const getFileTypeLabel = (type) => {
      const map = { 'image/png': 'PNG', 'image/jpeg': 'JPG', 'image/svg+xml': 'SVG', 'image/webp': 'WEBP' };
      return map[type] || type.split('/')[1]?.toUpperCase() || 'IMG';
    };

    const processFile = async (file) => {
      setLogoValidationError(null);
      const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setLogoValidationError(t('companyAdmins.unsupportedFormat'));
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setLogoValidationError(t('companyAdmins.fileTooLarge'));
        return;
      }
      const { width, height } = await getImageInfo(file);
      setLogoFileInfo({ name: file.name, size: formatBytes(file.size), type: getFileTypeLabel(file.type), width, height });
      setSelectedLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) processFile(file);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    };

    const handleUpload = async () => {
      if (!selectedLogoFile || !selectedCompany) return;
      setIsLogoUploading(true);
      const formData = new FormData();
      // Rename file to {companyname}-logo.{ext}
      const companySlug = (activeCompany?.name || 'company')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const ext = selectedLogoFile.name.split('.').pop() || 'png';
      const renamedFile = new File([selectedLogoFile], `${companySlug}-logo.${ext}`, { type: selectedLogoFile.type });
      formData.append('logo', renamedFile);
      try {
        const response = await api.patch(`/companies/${selectedCompany}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setCompanies(prev => prev.map(c => c.id === selectedCompany ? { ...c, logo: response.data.logo } : c));
        setSnackbar({ open: true, message: t('companyAdmins.logoUpdateSuccess'), severity: 'success' });
        setSelectedLogoFile(null);
        setLogoPreviewUrl(null);
        setLogoFileInfo(null);
        window.dispatchEvent(new CustomEvent('company-data-updated'));
      } catch (err) {
        console.error('Error uploading logo:', err);
        setSnackbar({ open: true, message: t('companyAdmins.logoUpdateFailed'), severity: 'error' });
      } finally {
        setIsLogoUploading(false);
      }
    };

    const handleResetLogo = async () => {
      if (!selectedCompany) return;
      if (!window.confirm(t('companyAdmins.logoResetConfirm'))) return;
      try {
        await api.patch(`/companies/${selectedCompany}/`, { logo: null });
        setCompanies(prev => prev.map(c => c.id === selectedCompany ? { ...c, logo: null } : c));
        setSnackbar({ open: true, message: t('companyAdmins.logoResetSuccess'), severity: 'success' });
        window.dispatchEvent(new CustomEvent('company-data-updated'));
      } catch (err) {
        setSnackbar({ open: true, message: t('companyAdmins.logoResetFailed'), severity: 'error' });
      }
    };


    const currentLogoUrl = logoPreviewUrl || (activeCompany?.logo ? resolveMediaUrl(activeCompany.logo) : null);
    return (
      <div className="p-6">
        {!selectedCompany || loading ? null : (
          <>
            {/* ── UPLOAD SECTION ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Preview Panel */}
              <div className="space-y-4">
                {/* Mode Toggle */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-600">{t('companyAdmins.previewMode')}</span>
                  <button
                    onClick={() => setLogoMode('light')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${logoMode === 'light' ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    <Sun size={13} /> {t('companyAdmins.light')}
                  </button>
                  <button
                    onClick={() => setLogoMode('dark')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${logoMode === 'dark' ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    <Moon size={13} /> {t('companyAdmins.dark')}
                  </button>
                </div>

                {/* Logo Preview Box */}
                <div className={`rounded-2xl border-2 border-dashed p-8 min-h-[200px] flex items-center justify-center transition-colors ${logoMode === 'dark' ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'}`}>
                  {currentLogoUrl ? (
                    <img src={currentLogoUrl} alt="Logo Preview" className="max-h-40 max-w-full object-contain" />
                  ) : (
                    <div className="text-center">
                      <Image size={48} className={`mx-auto mb-2 ${logoMode === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`text-sm ${logoMode === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{t('companyAdmins.noLogoUploaded')}</p>
                    </div>
                  )}
                </div>

                {/* Navbar Preview */}
                <div>
                  <button
                    onClick={() => setShowNavbarPreview(v => !v)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    <Eye size={15} /> {showNavbarPreview ? t('companyAdmins.hideNavbarPreview') : t('companyAdmins.showNavbarPreview')}
                  </button>
                  {showNavbarPreview && (
                    <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                      {/* Simulated Navbar */}
                      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-100">
                        {/* Aptiview Platform Logo */}
                        <img src={AptiviewLogo} alt="Aptiview Logo" className="h-6 object-contain" />
                        
                        {/* Separator and Custom Company Logo */}
                        {currentLogoUrl && (
                          <>
                            <div className="h-5 w-px bg-gray-300 mx-1" />
                            <img src={currentLogoUrl} alt="Company Logo" className="h-6 object-contain max-w-[120px]" />
                          </>
                        )}
                        <div className="ml-auto flex gap-3">
                          <div className="w-16 h-2 bg-gray-200 rounded-full" />
                          <div className="w-10 h-2 bg-gray-200 rounded-full" />
                          <div className="w-12 h-2 bg-gray-200 rounded-full" />
                        </div>
                      </div>
                      {/* Simulated Sidebar */}
                      <div className="flex">
                        <div className="w-14 bg-gray-50 border-r border-gray-100 flex flex-col items-center py-3 gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400">
                            <Briefcase size={16} className="opacity-70" />
                          </div>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400">
                            <BarChart2 size={16} className="opacity-70" />
                          </div>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600 border border-blue-100">
                            <Settings size={16} />
                          </div>
                          <div className="w-6 h-1 bg-gray-200 rounded-full" />
                          <div className="w-6 h-1 bg-gray-200 rounded-full" />
                        </div>
                        <div className="flex-1 bg-gray-50 p-3 min-h-[60px]">
                          <div className="w-3/4 h-2 bg-gray-200 rounded mb-2" />
                          <div className="w-1/2 h-2 bg-gray-200 rounded" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* File Info Badge */}
                {logoFileInfo && !logoValidationError && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    <span className="font-semibold text-green-700">{logoFileInfo.type}</span>
                    <span className="text-green-600">|</span>
                    <span className="text-green-700">{logoFileInfo.size}</span>
                    {logoFileInfo.width > 0 && (
                      <>
                        <span className="text-green-600">|</span>
                        <span className="text-green-700">{logoFileInfo.width}×{logoFileInfo.height}px</span>
                      </>
                    )}
                  </div>
                )}
                {logoValidationError && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm">
                    <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                    <span className="text-red-700">{logoValidationError}</span>
                  </div>
                )}
              </div>

              {/* Right: Upload + Actions */}
              <div className="flex flex-col gap-4">
                {/* Drag & Drop Zone */}
                <div
                  className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all ${isDraggingOver ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'}`}
                  onClick={() => document.getElementById('logo-upload-input').click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                  onDragLeave={() => setIsDraggingOver(false)}
                  onDrop={handleDrop}
                >
                  <input id="logo-upload-input" type="file" className="hidden" accept=".png,.jpg,.jpeg,.svg" onChange={handleFileChange} />
                  <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-transform ${isDraggingOver ? 'bg-blue-200 scale-110' : 'bg-blue-100'}`}>
                    <CloudUpload size={28} className="text-blue-600" />
                  </div>
                  <p className="text-base font-semibold text-gray-800 mb-1">
                    {isDraggingOver ? t('companyAdmins.dropHere') : selectedLogoFile ? selectedLogoFile.name : t('companyAdmins.dragDropImage')}
                  </p>
                  <p className="text-sm text-gray-500">{t('companyAdmins.orClickBrowse')}</p>
                  {isDraggingOver && (
                    <div className="absolute inset-0 rounded-2xl bg-blue-500 bg-opacity-10 pointer-events-none" />
                  )}
                </div>

                {/* Crop/Resize Note */}
                {selectedLogoFile && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
                    <Crop size={15} className="text-amber-600 flex-shrink-0" />
                    <span className="text-amber-700">
                      <strong>{t('companyAdmins.tipLabel')}:</strong> {t('companyAdmins.logoTip')}
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleUpload}
                    disabled={!selectedLogoFile || isLogoUploading}
                    className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white transition-all text-sm ${!selectedLogoFile || isLogoUploading ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-sky-500 to-fuchsia-500 hover:shadow-lg hover:scale-[1.02]'}`}
                  >
                    {isLogoUploading ? <><RefreshCw size={16} className="animate-spin" /> {t('companyAdmins.uploading')}</> : <><CloudUpload size={16} /> {activeCompany?.logo ? t('companyAdmins.replaceLogo') : t('companyAdmins.uploadLogo')}</>}
                  </button>
                  {selectedLogoFile && (
                    <button
                      onClick={() => { setSelectedLogoFile(null); setLogoPreviewUrl(null); setLogoFileInfo(null); setLogoValidationError(null); }}
                      className="px-5 py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all text-sm"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Reset to Default */}
                {activeCompany?.logo && (
                  <button
                    onClick={handleResetLogo}
                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-all text-sm"
                  >
                    <RotateCcw size={15} /> {t('companyAdmins.resetToDefaultLogo')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  const renderUsersTab = () => {
    return (
      <div className="p-6">
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                {t('companyAdmins.searchUsers')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder={t('companyAdmins.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                {t('companyAdmins.roleFilter')}
              </label>
              <select
                value={roleFilter}
                onChange={(e) => handleRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-sm"
              >
                <option value="all">{t('companyAdmins.allRoles')}</option>
                <option value="admin">{t('companyAdmins.admin')}</option>
                <option value="employee">{t('companyAdmins.employee')}</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium opacity-0">
                {t('companyAdmins.clear')}
              </label>
              {(searchTerm || roleFilter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <X size={16} />
                  {t('companyAdmins.clearFilters')}
                </button>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || roleFilter !== 'all') && (
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <div className="flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  <Search size={12} />
                  {t('companyAdmins.search')} "{searchTerm}"
                </div>
              )}
              {roleFilter !== 'all' && (
                <div className="flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                  <Filter size={12} />
                  {t('companyAdmins.role')} {roleFilter}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {!selectedCompany || loading ? null : (
          <>
            {/* Statistics Cards */}
            <CompanyStatsCards stats={stats} />

            {/* Users Table/Cards */}
            {sortedUsers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">{t('companyAdmins.noUsersFound')}</p>
                <p className="text-sm mt-1">
                  {searchTerm || roleFilter !== 'all'
                    ? t('companyAdmins.adjustFilters')
                    : t('companyAdmins.noUsersAdded')
                  }
                </p>
              </div>
            ) : isMobile ? (
              <CompanyUsersCards
                users={sortedUsers}
                onSort={handleSort}
                sortConfig={sortConfig}
                onDelete={(userId, userName) => openDeleteDialog(userId, userName)}
                onToggleUserManager={handleToggleUserManager}
                onOpenLoginActivity={(userId, userName) =>
                  setLoginActivityModal({
                    open: true,
                    userId,
                    userName: userName || '',
                    companyId: selectedCompany ?? null
                  })
                }
                onOpenTimeline={(userId, userName) => {
                  setTimelineUserId(userId);
                  setTimelineUserName(userName || '');
                  setTimelineOpen(true);
                }}
                isUserManager={!!userDetails?.is_usermanager}
                currentUserId={userDetails?.id}
                companyId={selectedCompany}
              />
            ) : (
              <CompanyUsersTable
                users={sortedUsers}
                onSort={handleSort}
                sortConfig={sortConfig}
                onDelete={(userId, userName) => openDeleteDialog(userId, userName)}
                onToggleUserManager={handleToggleUserManager}
                onOpenLoginActivity={(userId, userName) =>
                  setLoginActivityModal({
                    open: true,
                    userId,
                    userName: userName || '',
                    companyId: selectedCompany ?? null
                  })
                }
                onOpenTimeline={(userId, userName) => {
                  setTimelineUserId(userId);
                  setTimelineUserName(userName || '');
                  setTimelineOpen(true);
                }}
                isUserManager={!!userDetails?.is_usermanager}
                currentUserId={userDetails?.id}
                companyId={selectedCompany}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <PageShell fill={false}>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
      <PageCard overflow={false}>
          <PageHeader
            title={
              activeTab === 'companyLogo'
                ? t('nav.branding', 'Branding')
                : activeTab === 'languages'
                  ? t('nav.localization', 'Localization')
                  : t('companyAdmins.pageTitle')
            }
            subtitle={
              activeTab === 'companyLogo'
                ? t('companyAdmins.companyLogoDescription')
                : activeTab === 'languages'
                  ? t('companyAdmins.languagesSectionDescription')
                  : t('companyAdmins.pageSubtitle')
            }
          >
            {activeTab === 'languages' && currentUiLanguage && (
              <button
                type="button"
                onClick={() => setLangModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-white text-gray-700 shadow-sm transition-all duration-200 hover:text-sky-700 flex-shrink-0"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {currentUiLanguage.flag && (
                  <img
                    src={currentUiLanguage.flag}
                    width="22"
                    height="22"
                    alt=""
                    className="rounded-full object-cover border border-gray-200"
                  />
                )}
                <span>
                  {currentUiLanguage.name} ({currentUiLanguage.code.toUpperCase()})
                </span>
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            )}
          </PageHeader>
          {activeTab !== 'companyLogo' && activeTab !== 'languages' && renderSectionHeader()}

          {/* Tab Content */}
          <div className="bg-white">
            {activeTab === 'users' && renderUsersTab()}
            {activeTab === 'credits' && <CreditsTab />}
            {activeTab === 'languages' && renderLanguagesTab()}
            {activeTab === 'companyLogo' && renderCompanyLogoTab()}
          </div>
      </PageCard>

      <InviteAdminModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleSendInvite}
        companyName={companies.find(c => c.id === selectedCompany)?.name}
      />

      <LanguageSelectionModal
        open={langModalOpen}
        onClose={() => setLangModalOpen(false)}
        currentLanguage={i18n.language}
        availableLanguages={userLanguagesForModal}
        onSelectLanguage={async (code) => {
          await i18n.changeLanguage(code);
          setDocumentDirection(code);
          setTimeout(() => {
            window.dispatchEvent(new Event('languagechange'));
          }, 100);
          setLangModalOpen(false);
        }}
      />

      <UserLoginActivityModal
        open={loginActivityModal.open}
        onClose={() => setLoginActivityModal({ open: false, userId: null, userName: '', companyId: null })}
        userId={loginActivityModal.userId}
        userName={loginActivityModal.userName}
        companyId={loginActivityModal.companyId}
      />

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null, userName: '' })}
        onConfirm={handleConfirmDelete}
        title={t('companyAdmins.removeAdminTitle', 'Remove Admin')}
        message={t('companyAdmins.removeAdminConfirm', 'Are you sure you want to remove {{name}} as an admin from this company?', { name: deleteDialog.userName })}
      />

      {/* Timeline Drawer */}
      <Timeline
        open={timelineOpen}
        onClose={() => {
          setTimelineOpen(false);
          setTimelineUserId(null);
          setTimelineUserName('');
        }}
        apiEndpoint="audit/timeline/"
        apiParams={{
          module: 'companyadmin',
          ...(timelineUserId ? { entity_id: timelineUserId } : { company_id: selectedCompany })
        }}
        title={
          timelineUserName
            ? t('companyAdmins.userTimeline', { name: timelineUserName, defaultValue: `${timelineUserName} - Timeline` })
            : t('companyAdmins.adminTimeline', { defaultValue: 'Admin Timeline' })
        }
        pageName={t('companyAdmins.pageTitle')}
        width={450}
      />

      {/* Fixed Timeline Button */}
      {!isMobile && activeTab === 'users' && (
        <button
          onClick={() => {
            setTimelineUserId(null);
            setTimelineUserName('');
            setTimelineOpen(true);
          }}
          className={`fixed ${isRTL ? 'left-6' : 'right-6'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-3 rounded-2xl transition-all duration-300 group hover:scale-110 hover:shadow-2xl`}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
          }}
        >
          <Clock size={16} />
          <span className={`absolute ${isRTL ? 'left-full ml-4' : 'right-full mr-4'} px-3 py-2 bg-gray-900/90 backdrop-blur-sm text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg`}>
            {t('companyAdmins.timeline', { defaultValue: 'Timeline' })}
          </span>
        </button>
      )}
    </PageShell>
  );
};

export default CompanyAdmins;